import type {
    TranslationRecord,
    Translation,
    TranslationDocument,
    TranslationSegment,
} from './schemas';

function toSegment(s: TranslationSegment): Translation {
    return s._type === 'variable'
        ? { type: 'interpolated', variableName: s.variableName }
        : { type: 'string', value: s.value };
}

/** Returns a sorted list of all language codes found in the documents. */
export function extractLanguages(docs: TranslationDocument[]): string[] {
    return [
        ...new Set(docs.flatMap((d) => d.translation.map((t) => t._key))),
    ].sort();
}

/** Returns all unique variable names used across a document's translations. */
export function extractVariables(doc: TranslationDocument): string[] {
    return [
        ...new Set(
            doc.translation
                .flatMap((e) => e.translations)
                .filter((s) => s._type === 'variable')
                .map((s) => s.variableName),
        ),
    ];
}

/** Renders segments as a human-readable preview, e.g. `Hei, {name}!` */
export function buildPreview(segments: TranslationSegment[]): string {
    return segments
        .map((s) => (s._type === 'variable' ? `{${s.variableName}}` : s.value))
        .join('');
}

/** Transforms Sanity documents into a flat translation map for one language. */
export function transformTranslations(
    language: string,
    documents: TranslationDocument[],
): TranslationRecord {
    const result: Record<string, Translation[]> = {};
    for (const doc of documents) {
        const entry = doc.translation.find((t) => t._key === language);
        if (entry) {
            result[doc.key] = entry.translations.map(toSegment);
        }
    }
    return result;
}
